//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/core/services/service';
import { ApiModel, ApiArrayModel } from 'src/app/core/models/model';
import ImageDetailsModel from '../models/images-details.model';

/**
 * @service ImageDetailsService
 * @description Service for managing image-related operations within the application.
 * This service handles the retrieval, upload, download, and deletion of images associated with
 * farm sites, ponds, and cultures. It interacts with the backend API to manage image metadata
 * and binary data, often interfacing with S3 or local storage via the backend.
 * Extends the base Service class with generic type ImageDetailsModel.
 *
 * @feature 1. Retrieve image details and counts for farm sites
 * @feature 2. Upload and download images (including S3 integration)
 * @feature 3. Delete images and associated pond records
 * @feature 4. Cancel ongoing HTTP requests to prevent memory leaks
 *
 * @architecture
 * Extends base Service<ImageDetailsModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /blubay/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class ImageDetailsService extends Service<ImageDetailsModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/');
  }

  /**
   * @method deletePond
   * @async
   * @description Delete pond records, potentially associated with images.
   *
   * @param {Array<string>} pondId - Array of pond IDs to delete
   * @param {string} pondDelete - Flag or identifier indicating delete action scope
   * @returns {Promise<Array<ImageDetailsModel>>} Promise resolving to the operation result
   * @throws {HttpErrorResponse} Rejects promise if the request fails
   * @side-effect Adds request subscription to ongoingRequests
   */
  public deletePond(pondId: Array<string>, pondDelete: string) {
    return new Promise<Array<ImageDetailsModel>>((resolve, reject) => {
      const request = this.httpClient.post<ApiArrayModel<ImageDetailsModel>>(`${this.baseUrl}${this.serviceUrl}ponddelete/deletePond`, {
        pond_id: pondId, pond_delete: '' + pondDelete
      }, this.httpOptions).subscribe(response => {
        if (response.status) {
          resolve(response.data);
        }
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPonds
   * @async
   * @description Retrieve record counts or details for specific ponds.
   *
   * @param {Array<string>} pondId - Array of pond IDs to query
   * @returns {Promise<Array<ImageDetailsModel>>} Promise resolving to the pond data
   * @throws {HttpErrorResponse} Rejects promise if the request fails
   * @side-effect Adds request subscription to ongoingRequests
   */
  public getPonds(pondId: Array<string>) {
    return new Promise<Array<ImageDetailsModel>>((resolve, reject) => {
      const request = this.httpClient.post<ApiArrayModel<ImageDetailsModel>>(`${this.baseUrl}${this.serviceUrl}records/recordsCount`, {
        pond_id: pondId
      }, this.httpOptions).subscribe(response => {
        if (response.status) {
          resolve(response.data);
        }
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @returns {void}
   * @side-effect Aborts all pending HTTP requests and clears ongoingRequests array
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
